import { AiChatProviderName } from "../../../../../services/ai-chat-service";
import type {
  AttachedFile,
  AttachedImage,
  ChatMode,
  ChatTab,
  EffortTier,
  MentionReference,
  SlashCommandDefinition
} from "../shared/mcp-chat-types";
import type { McpChatInputAreaProps } from "./mcp-chat-input-area";

type InputAreaController = {
  state: {
    isInspectorActive: boolean;
    remainingBalance: number | null | undefined;
    isBalancePanelOpen: boolean;
    serverProvider: AiChatProviderName | null;
    projectAnalysisAvailable: boolean;
  };
  canUseInspector: () => boolean;
  handleSendFromUI: () => void;
  handleStop: () => void;
  handleInputChange: (value: string) => void;
  handleAttachImages: (images: AttachedImage[]) => void;
  handleRemoveImage: (imageId: string) => void;
  handleAttachFiles: (files: AttachedFile[]) => void;
  handleRemoveFile: (fileId: string) => void;
  handleToggleInspector: () => void;
  handleRemoveSelectedElement: (kuikaId: string) => void;
  handleSlashCommand: (cmd: SlashCommandDefinition, args: string) => void;
  handleSendToLlm: (prompt: string) => void;
  handleChatModeChange: (mode: ChatMode) => void;
  handleModelTierChange: (effort: EffortTier) => void;
  handlePlanModeToggle: () => void;
  handleMentionsChange: (mentions: MentionReference[], counter: number) => void;
  handleHistoryUp: () => boolean;
  handleHistoryDown: () => boolean;
  addCurrentSelection: () => boolean;
  handleImprovePrompt: () => void;
  handleCancelImprovePrompt: () => void;
  handleBalancePanelToggle: () => void;
  sendProjectAnalysisRequest: () => void;
};

export function buildMcpChatInputAreaProps(
  panel: InputAreaController,
  activeTab: ChatTab
): Omit<McpChatInputAreaProps, "ref"> {
  // Credits only exist on the platform (OpenRouter) provider. On-prem
  // deployments (anthropic / openai / openai-compatible) have no billing,
  // so the balance pill is hidden — same gate as the model selector.
  const isCreditVisible: boolean = panel.state.serverProvider === AiChatProviderName.OpenRouter;
  return {
    inputValue: activeTab.inputValue,
    isLoading: activeTab.isLoading,
    attachedImages: activeTab.attachedImages,
    attachedFiles: activeTab.attachedFiles,
    selectedElementAttachments: activeTab.selectedElementAttachments,
    chatMode: activeTab.chatMode,
    isInspectorActive: panel.state.isInspectorActive,
    isInspectorAvailable: panel.canUseInspector(),
    onSend: panel.handleSendFromUI,
    onStop: panel.handleStop,
    onInputChange: panel.handleInputChange,
    onAttachImages: panel.handleAttachImages,
    onRemoveImage: panel.handleRemoveImage,
    onAttachFiles: panel.handleAttachFiles,
    onRemoveFile: panel.handleRemoveFile,
    onSlashCommandSelect: panel.handleSlashCommand,
    onSendPrompt: panel.handleSendToLlm,
    onChatModeChange: panel.handleChatModeChange,
    // Effort selector shares the credits gate: it only makes sense on the
    // OpenRouter (platform) provider — on-prem collapses all tiers to one model.
    effort: activeTab.effort,
    onModelTierChange: isCreditVisible ? panel.handleModelTierChange : undefined,
    planMode: activeTab.planMode,
    onPlanModeToggle: panel.handlePlanModeToggle,
    onToggleInspector: panel.handleToggleInspector,
    onRemoveSelectedElement: panel.handleRemoveSelectedElement,
    isImprovingPrompt: activeTab.isImprovingPrompt,
    onImprovePrompt: panel.handleImprovePrompt,
    onCancelImprovePrompt: panel.handleCancelImprovePrompt,
    mentions: activeTab.mentions,
    mentionCounter: activeTab.mentionCounter,
    onMentionsChange: panel.handleMentionsChange,
    onHistoryUp: panel.handleHistoryUp,
    onHistoryDown: panel.handleHistoryDown,
    onAddCurrentSelection: panel.addCurrentSelection,
    remainingBalance: isCreditVisible ? panel.state.remainingBalance : undefined,
    isBalancePanelOpen: panel.state.isBalancePanelOpen,
    onBalancePanelToggle: isCreditVisible ? panel.handleBalancePanelToggle : undefined,
    // Undefined hides the control entirely — same gating style as the balance
    // pill and the effort selector. The server is the one that knows whether the
    // discovery surface exists on this deployment.
    onProjectAnalysis: panel.state.projectAnalysisAvailable ? panel.sendProjectAnalysisRequest : undefined
  };
}
